import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Img } from "@/components/site/Motion";

const Filters = ({ items, filters, value, onChange, testid }) => {
  const opts = [[null, "All", items.length], ...filters.map(([id, l]) => [id, l, items.filter((x) => x.tags?.includes(id)).length]).filter((o) => o[2] > 0)];
  if (opts.length < 3) return null;
  return (
    <div className="viewer-filters mx-auto flex w-full max-w-[1100px] gap-2 overflow-x-auto px-4 pt-4 md:flex-wrap md:px-8" role="toolbar" aria-label="Filter projects by system" data-testid={`${testid}-filters`}>
      {opts.map(([id, l, c]) => (
        <button key={l} type="button" onClick={() => onChange(id)} aria-pressed={value === id} className={`filter-pill ${value === id ? "is-on" : ""}`} data-testid={`${testid}-filter-${id || "all"}`}>
          {l} <span className="tabular-nums opacity-70">{c}</span>
        </button>
      ))}
    </div>
  );
};

// Full-size view layered over the grid: arrows, keyboard and swipe, loops at both ends.
const FullView = ({ items, index, onIndex, onBack, testid }) => {
  const n = items.length;
  const it = items[index];
  const touch = useRef(null);
  const step = (d) => onIndex((index + d + n) % n);
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
  };
  const onTouchEnd = (e) => {
    const x0 = touch.current;
    touch.current = null;
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0;
    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
  };
  return (
    <div className="viewer-full absolute inset-0 z-[2] flex flex-col" onKeyDown={onKey} onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd} data-testid={`${testid}-full`}>
      <div className="flex items-center justify-between gap-4 px-4 pt-[max(1rem,env(safe-area-inset-top))] md:px-8 md:pt-6">
        <button type="button" className="btn-glass btn-on-dark" onClick={onBack} autoFocus data-testid={`${testid}-back-btn`}><ArrowLeft aria-hidden="true" size={18} /> All photos</button>
        <div className="flex items-center gap-3">
          <p className="ext-head text-sm tabular-nums text-white" data-testid={`${testid}-counter`}>{index + 1} / {n}</p>
          <Dialog.Close className="btn-glass btn-on-dark btn-icon" aria-label="Close gallery" data-testid={`${testid}-full-close-btn`}><X aria-hidden="true" size={20} /></Dialog.Close>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 py-4 md:px-24">
        <Img key={it.name} name={it.name} alt={it.alt} w={it.w} h={it.h} sizes="100vw" priority className="lightbox-img max-h-full w-auto max-w-full rounded-[12px] object-contain shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]" />
        <button type="button" className="btn-glass btn-on-dark btn-icon absolute left-3 top-1/2 -translate-y-1/2 md:left-8" onClick={() => step(-1)} aria-label="Previous image" data-testid={`${testid}-prev-btn`}><ChevronLeft aria-hidden="true" size={22} /></button>
        <button type="button" className="btn-glass btn-on-dark btn-icon absolute right-3 top-1/2 -translate-y-1/2 md:right-8" onClick={() => step(1)} aria-label="Next image" data-testid={`${testid}-next-btn`}><ChevronRight aria-hidden="true" size={22} /></button>
      </div>
      <div className="ext-head px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-center md:pb-8">
        <p className="text-base font-semibold text-white md:text-lg">{it.system}</p>
        <p className="mt-1 text-sm text-[#E3EAF0]">{it.setting}</p>
      </div>
    </div>
  );
};

// Tapping a photo opens a scrollable 2-column preview grid; tapping a preview opens it full size.
// `start` is the tapped photo index (or null when closed); the grid scrolls to it.
export const Lightbox = ({ items, filters = [], start, onClose, testid = "lightbox" }) => {
  const open = start !== null;
  const [full, setFull] = useState(null);
  const [filter, setFilter] = useState(null);
  const grid = useRef(null);
  const shown = filter ? items.filter((x) => x.tags?.includes(filter)) : items;
  const pick = (id) => { setFilter(id); grid.current?.scrollTo({ top: 0 }); };
  useEffect(() => {
    if (!open) { setFull(null); setFilter(null); return undefined; }
    const t = setTimeout(() => grid.current?.querySelector(`[data-i="${start}"]`)?.scrollIntoView({ block: "center" }), 40);
    return () => clearTimeout(t);
  }, [open, start]);
  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="lightbox-overlay fixed inset-0 z-[95]" />
        <Dialog.Content
          onEscapeKeyDown={(e) => { if (full !== null) { e.preventDefault(); setFull(null); } }}
          className="lightbox-panel fixed inset-0 z-[96] flex flex-col text-white outline-none"
          data-testid={testid}
          aria-describedby={undefined}
        >
          <div className={`flex items-center justify-between gap-4 px-4 pt-[max(1rem,env(safe-area-inset-top))] md:px-8 md:pt-6 ${full !== null ? "invisible" : ""}`}>
            <Dialog.Title className="ext-head text-sm font-semibold uppercase tracking-[0.16em] text-white">Projects · {shown.length} photos</Dialog.Title>
            <Dialog.Close className="btn-glass btn-on-dark btn-icon" aria-label="Close gallery" data-testid={`${testid}-close-btn`}><X aria-hidden="true" size={20} /></Dialog.Close>
          </div>
          <div className={full !== null ? "invisible" : ""}><Filters items={items} filters={filters} value={filter} onChange={pick} testid={testid} /></div>
          <ul ref={grid} className={`viewer-grid mx-auto grid w-full max-w-[1100px] flex-1 grid-cols-2 content-start gap-2.5 overflow-y-auto overscroll-contain px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 md:gap-4 md:px-8 ${full !== null ? "invisible" : ""}`} data-testid={`${testid}-grid`}>
            {shown.map((it, i) => (
              <li key={it.name} data-i={i}>
                <button type="button" onClick={() => setFull(i)} className={`group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${!filter && i === start ? "viewer-picked" : ""}`} aria-label={`View full size: ${it.system}, ${it.setting}`} data-testid={`${testid}-thumb-${i}`}>
                  <span className="img-frame block aspect-[4/3]"><Img name={it.name} alt={it.alt} w={it.w} h={it.h} sizes="(min-width: 768px) 520px, 50vw" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" /></span>
                  <span className="ext-head mt-1.5 block truncate text-[12px] font-semibold text-white md:text-sm">{it.system}</span>
                </button>
              </li>
            ))}
          </ul>
          {full !== null && <FullView items={shown} index={full} onIndex={setFull} onBack={() => setFull(null)} testid={testid} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
